import type { QueryClient } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CustomerNoteCategory } from "./customerNotesApi";
import { deleteCustomerNote, getCustomerNotes } from "./customerNotesApi";
import { useSnackbar } from "notistack";
import { useAddCustomerNote } from "./useAddCustomerNote";

export function customerNotesKey(customerId: string, category: CustomerNoteCategory) {
  return ["customer", customerId, "notes", { category }];
}

export function invalidateCustomerNotesQueries(queryClient: QueryClient, customerId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["customers", customerId] }),
    queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey.at(0) === "customers" }),
    queryClient.invalidateQueries({ queryKey: ["customer", customerId, "notes"] }),
  ]);
}

export const useCustomerNotes = (customerId: string, category: CustomerNoteCategory) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: customerNotesKey(customerId, category),
    queryFn: ({ signal }) => getCustomerNotes(customerId, category, signal),
  });
  const { enqueueSnackbar } = useSnackbar();

  const addNote = useAddCustomerNote(customerId);

  const deleteNoteMutation = useMutation<void, unknown, string>({
    mutationFn: (id: string) => deleteCustomerNote(customerId, id),
    onSuccess: async () => {
      enqueueSnackbar("Notatka została usunięta", { variant: "success" });
      await invalidateCustomerNotesQueries(queryClient, customerId);
    },
  });

  return {
    notes: data ?? [],
    isLoading,
    addNote,
    deleteNote: (id: string) => deleteNoteMutation.mutateAsync(id),
  };
};
